import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Camera, X, RotateCcw, Check, AlertCircle, SwitchCamera } from 'lucide-react';

const CameraCapture = ({ onCapture, onClose, existingPhoto = null }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(existingPhoto);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' = arrière, 'user' = avant
  const [canSwitch, setCanSwitch] = useState(false);
  const isStartingRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (videoRef.current && !isStartingRef.current) {
        startCamera();
      } else if (!videoRef.current) {
        setError('Erreur: Élément vidéo non trouvé. Rechargez la page.');
        setLoading(false);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      stopCamera();
    };
  }, [facingMode]); // ✅ Redémarre quand facingMode change

  const startCamera = async () => {
    if (isStartingRef.current) return;
    isStartingRef.current = true;
    
    try {
      setLoading(true);
      setError(null);

      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log(`🎬 DÉMARRAGE CAMÉRA: ${facingMode === 'environment' ? 'ARRIÈRE' : 'AVANT'}`);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━');

      if (!videoRef.current) {
        throw new Error('videoRef null');
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('getUserMedia non supporté');
      }

      // ✅ STRATÉGIE 1 : Forcer facingMode strict
      console.log(`📸 Tentative 1: facingMode exact "${facingMode}"...`);
      try {
        const stream1 = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { exact: facingMode }, // ✅ STRICT
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        });
        console.log('✅ SUCCÈS avec facingMode exact');
        setCanSwitch(true); // ✅ Si ça marche, on peut basculer
        return await activateStream(stream1);
      } catch (err1) {
        console.log(`⚠️ Tentative 1 échouée: ${err1.name}`);
      }

      // ✅ STRATÉGIE 2 : Forcer facingMode non strict
      console.log(`📸 Tentative 2: facingMode "${facingMode}" (non exact)...`);
      try {
        const stream2 = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: facingMode, // ✅ MOINS STRICT
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        });
        console.log('✅ SUCCÈS avec facingMode non exact');
        setCanSwitch(true); // ✅ Si ça marche, on peut basculer
        return await activateStream(stream2);
      } catch (err2) {
        console.log(`⚠️ Tentative 2 échouée: ${err2.name}`);
      }

      // ❌ ÉCHEC : Pas de fallback vers n'importe quelle caméra !
      throw new Error(
        facingMode === 'environment'
          ? 'Caméra arrière non disponible sur cet appareil'
          : 'Caméra avant non disponible sur cet appareil'
      );

    } catch (err) {
      console.error('❌ Erreur complète:', err);
      
      let errorMessage = '';
      
      if (err.message.includes('non disponible')) {
        errorMessage = err.message;
        setCanSwitch(true); // ✅ Permettre de basculer vers l'autre caméra
      } else {
        switch (err.name) {
          case 'NotAllowedError':
          case 'PermissionDeniedError':
            errorMessage = `🚫 Accès caméra REFUSÉ

1. Cliquez cadenas 🔒 dans barre d'adresse
2. Autorisations → Caméra → Autoriser
3. Rechargez (Ctrl+R)`;
            break;
            
          case 'NotFoundError':
            errorMessage = '📷 Aucune caméra détectée';
            setCanSwitch(false);
            break;
            
          case 'NotReadableError':
            errorMessage = `⚠️ Caméra utilisée par autre app

Fermez: Zoom, Teams, autres onglets`;
            break;
            
          default:
            errorMessage = `❌ ${err.message}`;
        }
      }
      
      setError(errorMessage);
      setLoading(false);
    } finally {
      isStartingRef.current = false;
    }
  };

  const activateStream = async (mediaStream) => {
    if (!videoRef.current) {
      throw new Error('videoRef null pendant activation');
    }
    
    setStream(mediaStream);
    videoRef.current.srcObject = mediaStream;
    
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Timeout')), 5000);
      videoRef.current.onloadedmetadata = () => {
        clearTimeout(timeout);
        resolve();
      };
    });
    
    await videoRef.current.play();
    
    const track = mediaStream.getVideoTracks()[0];
    const settings = track.getSettings();
    console.log(`📊 ${settings.width}x${settings.height}`);
    console.log(`📸 ${track.label}`);
    console.log(`🎥 facingMode: ${settings.facingMode || 'non spécifié'}`);
    
    setLoading(false);
    console.log('✅ CAMÉRA PRÊTE');
    
    return mediaStream;
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const switchCamera = () => {
    console.log('🔄 Basculer caméra');
    stopCamera();
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      alert('Erreur: Élément manquant');
      return;
    }

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        async (blob) => {
          if (!blob) return;
          
          const compressedBlob = await compressImage(blob);
          const imageUrl = URL.createObjectURL(compressedBlob);
          
          setCapturedImage(imageUrl);
          stopCamera();
          
          console.log('✅ Photo capturée');
        },
        'image/jpeg',
        0.85
      );
    } catch (err) {
      console.error('❌ Erreur capture:', err);
      alert('Erreur: ' + err.message);
    }
  };

  const compressImage = async (blob) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        let width = img.width;
        let height = img.height;
        const maxSize = 1200;
        
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = (height / width) * maxSize;
            width = maxSize;
          } else {
            width = (width / height) * maxSize;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (compressedBlob) => {
            URL.revokeObjectURL(url);
            resolve(compressedBlob);
          },
          'image/jpeg',
          0.85
        );
      };
      
      img.src = url;
    });
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setLoading(true);
    setTimeout(() => startCamera(), 100);
  };

  const confirmPhoto = async () => {
    if (!capturedImage) return;

    try {
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      
      onCapture(blob);
      onClose();
    } catch (err) {
      console.error('❌ Erreur confirmation:', err);
      alert('Erreur: ' + err.message);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
    >
      <div className="bg-slate-900 rounded-xl max-w-4xl w-full overflow-hidden">
        {/* En-tête */}
        <div className="bg-slate-800 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Camera className="w-6 h-6 text-blue-400" />
            Prendre une photo
            <span className="text-sm text-gray-400 font-normal">
              ({facingMode === 'environment' ? '📷 Arrière' : '🤳 Avant'})
            </span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-6">
          {error ? (
            // Erreur
            <div className="text-center py-8">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                Erreur d'accès à la caméra
              </h3>
              <pre className="text-gray-300 mb-6 whitespace-pre-line text-left bg-slate-800 p-4 rounded text-sm">
                {error}
              </pre>
              
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => {
                    setError(null);
                    setLoading(true);
                    setTimeout(() => startCamera(), 100);
                  }}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all"
                >
                  Réessayer
                </button>
                
                {canSwitch && (
                  <button
                    onClick={switchCamera}
                    className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-all"
                  >
                    <SwitchCamera className="w-5 h-5" />
                    Basculer vers caméra {facingMode === 'environment' ? 'avant' : 'arrière'}
                  </button>
                )}
              </div>
            </div>
          ) : capturedImage ? (
            // Photo capturée
            <div className="space-y-4">
              <div className="relative">
                <img
                  src={capturedImage}
                  alt="Photo capturée"
                  className="w-full rounded-lg"
                />
                <div className="absolute top-2 right-2 bg-green-500 text-white px-3 py-1 rounded text-sm font-semibold">
                  ✓ Capturée
                </div>
              </div>
              
              <div className="flex gap-4">
                <button
                  onClick={retakePhoto}
                  className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center justify-center gap-2 transition-all"
                >
                  <RotateCcw className="w-5 h-5" />
                  Reprendre
                </button>
                <button
                  onClick={confirmPhoto}
                  className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2 transition-all"
                >
                  <Check className="w-5 h-5" />
                  Valider
                </button>
              </div>
            </div>
          ) : (
            // Vidéo en direct
            <div className="space-y-4">
              {loading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-white text-lg">
                    Démarrage caméra {facingMode === 'environment' ? 'arrière' : 'avant'}...
                  </p>
                  <p className="text-gray-400 text-sm mt-2">
                    Autorisez l'accès si demandé
                  </p>
                </div>
              )}
              
              <div className={`relative bg-black rounded-lg overflow-hidden ${loading ? 'hidden' : ''}`}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full"
                  style={{ maxHeight: '60vh' }}
                />
                
                {/* Grille */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="w-full h-full grid grid-cols-3 grid-rows-3 opacity-20">
                    {[...Array(9)].map((_, i) => (
                      <div key={i} className="border border-white"></div>
                    ))}
                  </div>
                </div>

                {/* Indicateur REC + Caméra */}
                <div className="absolute top-4 right-4 flex flex-col gap-2">
                  <div className="flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full text-sm">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    REC
                  </div>
                  <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs">
                    {facingMode === 'environment' ? '📷 Arrière' : '🤳 Avant'}
                  </div>
                </div>

                {/* Bouton basculer caméra */}
                {canSwitch && (
                  <button
                    onClick={switchCamera}
                    className="absolute top-4 left-4 bg-purple-600/80 hover:bg-purple-700 text-white p-3 rounded-full transition-all"
                    title="Basculer caméra"
                  >
                    <SwitchCamera className="w-5 h-5" />
                  </button>
                )}

                {/* Message */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-lg text-sm">
                  📸 Centrez l'outil dans le cadre
                </div>
              </div>

              {!loading && (
                <button
                  onClick={capturePhoto}
                  className="w-full px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center gap-2 transition-all text-lg font-semibold"
                >
                  <Camera className="w-6 h-6" />
                  Capturer la photo
                </button>
              )}
            </div>
          )}

          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      </div>
    </motion.div>
  );
};

export default CameraCapture;